// src/pages/AdminPage.jsx
import React, { useEffect, useState, useContext } from "react";
import { EventsContext } from "../services/EventsContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Vérification adresse / carte
import { geocodeAddressFR } from "../services/geocode";
import MapConfirmModal from "../components/MapConfirmModal";

// Utilitaires adresse
import {
  buildFullAddress,
  normalizeCity,
  haversineMeters,
} from "../services/adress";

// ⚠️ À sécuriser (variables d'environnement / proxy backend)
const GITHUB_TOKEN = "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";
const REPO_OWNER = "SebastienGuimety";
const REPO_NAME = "NewSiteCoing";
const EVENTS_FILE_PATH = "events.json";
const SALES_POINTS_FILE_PATH = "salesPoints.json";

// --- Base64 UTF-8 helpers ---
function encodeBase64(string) {
  return btoa(
    encodeURIComponent(string).replace(/%([0-9A-F]{2})/g, (match, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  );
}
const b64ToUtf8 = (b64) =>
  new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));

// --- Upload d'image vers GitHub (meme mecanisme que events.json) ---
const EVENTS_IMAGES_DIR = "public/assets/events";

// Nettoie le nom du fichier (accents, espaces, caracteres speciaux)
const slugifyFileName = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9.]+/g, "-")
    .replace(/-+/g, "-");

/*
 * Compresse/redimensionne l'image DANS LE NAVIGATEUR avant l'upload.
 * Le client peut donc envoyer une grosse photo (JPEG/PNG de plusieurs Mo) :
 * on la redimensionne (cote max ~1600px) et on la reencode en WebP (~qualite 0.82).
 * Resultat : un fichier leger (souvent < 250 Ko) sans aucune action du client.
 * Renvoie { base64, ext }.
 */
const compressImage = (file, maxDim = 1600, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const width = Math.round(img.width * scale);
      const height = Math.round(img.height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);

      const toBase64 = (blob) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve({
            base64: String(reader.result).split(",")[1],
            ext: blob.type === "image/webp" ? "webp" : "jpg",
          });
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      };

      // WebP en priorite ; fallback JPEG si le navigateur ne sait pas encoder le WebP
      canvas.toBlob(
        (blob) => {
          if (blob) return toBase64(blob);
          canvas.toBlob(
            (jpg) => (jpg ? toBase64(jpg) : reject(new Error("Compression echouee"))),
            "image/jpeg",
            quality
          );
        },
        "image/webp",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image illisible"));
    };
    img.src = url;
  });

// Compresse puis envoie l'image dans public/assets/events/, renvoie le chemin public
const uploadImageToGitHub = async (file) => {
  const { base64, ext } = await compressImage(file);
  const base = slugifyFileName(file.name).replace(/\.[a-z0-9]+$/i, "") || "image";
  const safeName = `${Date.now()}-${base}.${ext}`;
  const path = `${EVENTS_IMAGES_DIR}/${safeName}`;

  const response = await fetch(
    `https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/contents/${path}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${GITHUB_TOKEN}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: `Ajout image evenement ${safeName}`,
        content: base64, // base64 brut (image deja compressee)
      }),
    }
  );

  if (!response.ok) throw new Error(`Upload image echoue (${response.status})`);

  // Repo PRIVE : raw.githubusercontent demande une auth -> inutilisable dans un <img>.
  // On renvoie le chemin local : l'image est servie par TON site une fois deploye
  // (public/ -> racine du build). Le repo prive n'impacte pas le site public.
  return `/assets/events/${safeName}`;
};

// --- GitHub helpers ---
const fetchGitHubFile = async (filePath) => {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/contents/${filePath}`,
      { headers: { Authorization: `Bearer ${GITHUB_TOKEN}` } }
    );
    if (!response.ok) throw new Error(`GitHub ${response.status}`);
    const data = await response.json();
    return {
      content: JSON.parse(b64ToUtf8(data.content)),
      sha: data.sha,
    };
  } catch (error) {
    toast.error("Erreur lors de la récupération du fichier.");
    console.error(error);
    return { content: [], sha: null };
  }
};

const updateGitHubFile = async (filePath, newContent, sha) => {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/contents/${filePath}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${GITHUB_TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: `Mise à jour de ${filePath}`,
          content: encodeBase64(JSON.stringify(newContent, null, 2)),
          sha,
        }),
      }
    );
    if (!response.ok) throw new Error("Erreur lors de la mise à jour du fichier.");
    toast.success("Fichier mis à jour avec succès !");
  } catch (error) {
    toast.error("Erreur lors de la mise à jour du fichier.");
    console.error(error);
  }
};

const AdminPage = () => {
  // --- UI states ---
  const [loading, setLoading] = useState(false);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [salesPointSubmitting, setSalesPointSubmitting] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [deletingSalesPointId, setDeletingSalesPointId] = useState(null);

  // --- Vérification adresse / carte ---
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // --- Auth basique ---
  const [authenticated, setAuthenticated] = useState(
    localStorage.getItem("adminAuth") === "true"
  );
  const [password, setPassword] = useState("");
  const adminPassword = "mdp"; // ⚠️ à déplacer en env si tu gardes ce mécanisme

  // --- Contexte ---
  const { events, setEvents, salesPoints, setSalesPoints } =
    useContext(EventsContext);

  // --- Events form ---
  const [newEvent, setNewEvent] = useState({
    title: "",
    startDate: "",
    endDate: "",
    time: "",
    location: "",
    description: "",
    image: "",
  });

  // --- Image evenement ---
  const [imagePreview, setImagePreview] = useState("");
  const [imageUploading, setImageUploading] = useState(false);

  // --- SalesPoint form (nouveau schéma structuré) ---
  const [newSalesPoint, setNewSalesPoint] = useState({
    name: "",
    category: "",
    address1: "",
    address2: "",
    postalCode: "",
    city: "",
    country: "France",
    lat: null,
    lng: null,
    place_id: "",
    formattedAddress: "",
  });

  // --- Search ---
  const [eventSearch, setEventSearch] = useState("");
  const [salesPointSearch, setSalesPointSearch] = useState("");

  // --- Fetch initial data ---
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const eventsData = await fetchGitHubFile(EVENTS_FILE_PATH);
      setEvents(eventsData.content);
      const salesPointsData = await fetchGitHubFile(SALES_POINTS_FILE_PATH);
      setSalesPoints(salesPointsData.content);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Helpers ---
  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
  };

  const isProbableDuplicate = () => {
    if (typeof newSalesPoint.lat !== "number" || typeof newSalesPoint.lng !== "number")
      return false;
    return salesPoints.some((p) => {
      if (typeof p.lat !== "number" || typeof p.lng !== "number") return false;
      const samePC =
        (p.postalCode || "").trim() === (newSalesPoint.postalCode || "").trim();
      const dist = haversineMeters(
        { lat: p.lat, lng: p.lng },
        { lat: newSalesPoint.lat, lng: newSalesPoint.lng }
      );
      return samePC && dist < 100;
    });
  };

  // --- Auth actions ---
  const handleLogin = () => {
    if (password === adminPassword) {
      setAuthenticated(true);
      localStorage.setItem("adminAuth", "true");
    } else {
      alert("Mot de passe incorrect");
    }
  };
  const handleLogout = () => {
    setAuthenticated(false);
    localStorage.removeItem("adminAuth");
  };

  // --- Events submit/delete ---
  const handleEventImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Garde-fou large : on accepte les grosses photos (elles seront compressees
    // automatiquement avant l'envoi). On bloque seulement l'extreme (> 25 Mo).
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Image vraiment trop lourde (max 25 Mo).");
      e.target.value = "";
      return;
    }

    // Apercu local immediat (avant meme la fin de l'upload)
    setImagePreview(URL.createObjectURL(file));

    try {
      setImageUploading(true);
      const path = await uploadImageToGitHub(file);
      setNewEvent((prev) => ({ ...prev, image: path }));
      toast.success("Image ajoutee !");
    } catch (error) {
      toast.error("Erreur lors de l'upload de l'image.");
      console.error(error);
      setImagePreview("");
    } finally {
      setImageUploading(false);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.startDate || !newEvent.location) {
      toast.error("Veuillez remplir tous les champs obligatoires pour l'événement.");
      return;
    }
    setEventSubmitting(true);
    try {
      const updatedEvents = [
        ...events,
        {
          ...newEvent,
          id: events.length + 1,
          duration:
            newEvent.startDate && newEvent.endDate
              ? calculateDuration(newEvent.startDate, newEvent.endDate)
              : 1,
        },
      ];
      const { sha } = await fetchGitHubFile(EVENTS_FILE_PATH);
      await updateGitHubFile(EVENTS_FILE_PATH, updatedEvents, sha);
      setEvents(updatedEvents);
      toast.success("Événement ajouté avec succès !");
      setNewEvent({
        title: "",
        startDate: "",
        endDate: "",
        time: "",
        location: "",
        description: "",
        image: "",
      });
      setImagePreview("");
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'événement.");
    } finally {
      setEventSubmitting(false);
    }
  };

  const handleDeleteEvent = async (id) => {
    setDeletingEventId(id);
    try {
      const updatedEvents = events.filter((event) => event.id !== id);
      const { sha } = await fetchGitHubFile(EVENTS_FILE_PATH);
      await updateGitHubFile(EVENTS_FILE_PATH, updatedEvents, sha);
      setEvents(updatedEvents);
      toast.success("Événement supprimé avec succès !");
    } catch (error) {
      toast.error("Erreur lors de la suppression de l'événement.");
    } finally {
      setDeletingEventId(null);
    }
  };

  // --- Vérification adresse / carte ---
  const handleVerifyAddress = async (e) => {
    e.preventDefault();
    const full = buildFullAddress(newSalesPoint);
    try {
      const res = await geocodeAddressFR(full);
      setCandidates(res);
      setSelectedCandidate(res[0]);
      setVerifyOpen(true);
    } catch (err) {
      toast.error("Adresse introuvable. Vérifiez les champs.");
    }
  };

  const applyConfirmation = (geo) => {
    setNewSalesPoint((prev) => ({
      ...prev,
      address1: geo.address1 || prev.address1,
      postalCode: geo.postalCode || prev.postalCode,
      city: geo.city ? normalizeCity(geo.city) : prev.city,
      country: geo.country || prev.country,
      lat: geo.lat,
      lng: geo.lng,
      place_id: geo.place_id,
      formattedAddress: geo.formattedAddress,
    }));
    setVerifyOpen(false);
  };

  // --- Sales point submit/delete ---
  const handleSalesPointSubmit = async (e) => {
    e.preventDefault();

    // Validation nouveaux champs
    if (!newSalesPoint.name || !newSalesPoint.address1 || !newSalesPoint.postalCode || !newSalesPoint.city) {
      toast.error("Nom, adresse, code postal et ville sont obligatoires.");
      return;
    }

    setSalesPointSubmitting(true);
    try {
      // Si pas de lat/lng → on déclenche la vérification sur carte
      if (typeof newSalesPoint.lat !== "number" || typeof newSalesPoint.lng !== "number") {
        const full = buildFullAddress(newSalesPoint);
        const res = await geocodeAddressFR(full).catch(() => null);
        if (!res || !res[0]) {
          setSalesPointSubmitting(false);
          toast.error("Adresse introuvable. Clique sur « Vérifier sur la carte » pour placer le point.");
          setVerifyOpen(true);
          return;
        }
        setCandidates(res);
        setSelectedCandidate(res[0]);
        setSalesPointSubmitting(false);
        setVerifyOpen(true);
        return; // l’utilisateur confirme puis soumet à nouveau
      }

      // Objet à sauvegarder (avec rétro-compat address/city)
      const pointToSave = {
        ...newSalesPoint,
        id: salesPoints.length + 1,
        address: [newSalesPoint.address1, newSalesPoint.address2].filter(Boolean).join(", "),
        city: newSalesPoint.city,
      };

      const updatedSalesPoints = [...salesPoints, pointToSave];
      const { sha } = await fetchGitHubFile(SALES_POINTS_FILE_PATH);
      await updateGitHubFile(SALES_POINTS_FILE_PATH, updatedSalesPoints, sha);
      setSalesPoints(updatedSalesPoints);

      toast.success("Point de vente ajouté avec succès !");
      setNewSalesPoint({
        name: "",
        category: "",
        address1: "",
        address2: "",
        postalCode: "",
        city: "",
        country: "France",
        lat: null,
        lng: null,
        place_id: "",
        formattedAddress: "",
      });
    } catch (error) {
      toast.error("Erreur lors de l'ajout du point de vente.");
    } finally {
      setSalesPointSubmitting(false);
    }
  };

  const handleDeleteSalesPoint = async (id) => {
    setDeletingSalesPointId(id);
    try {
      const updatedSalesPoints = salesPoints.filter((point) => point.id !== id);
      const { sha } = await fetchGitHubFile(SALES_POINTS_FILE_PATH);
      await updateGitHubFile(SALES_POINTS_FILE_PATH, updatedSalesPoints, sha);
      setSalesPoints(updatedSalesPoints);
      toast.success("Point de vente supprimé avec succès !");
    } catch (error) {
      toast.error("Erreur lors de la suppression du point de vente.");
    } finally {
      setDeletingSalesPointId(null);
    }
  };

  // --- Filters ---
  const filteredEvents = events.filter((event) =>
    (event.title || "").toLowerCase().includes(eventSearch.toLowerCase())
  );

  const filteredSalesPoints = salesPoints.filter((point) => {
    const q = salesPointSearch.toLowerCase();
    return (
      (point.name || "").toLowerCase().includes(q) ||
      (point.city || "").toLowerCase().includes(q) ||
      (point.postalCode || "").toLowerCase().includes(q)
    );
  });

  // --- Login ---
  if (!authenticated) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ backgroundColor: '#FBF7EF' }}>
        <div className="p-8 bg-white rounded-2xl border border-[rgba(120,90,40,0.15)] shadow-md w-80">
          <h1 className="font-sans text-2xl text-stone-900 mb-5">Admin Login</h1>
          <input
            type="password"
            placeholder="Mot de passe admin"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition mb-4"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            onClick={handleLogin}
            className="bg-[#2E2318] text-white py-2.5 px-5 rounded-xl hover:opacity-90 transition"
          >
            Connexion
          </button>
        </div>
      </div>
    );
  }

  // --- Page ---
  return (
    <div className="p-6 md:p-10 min-h-screen mt-20 md:mt-28" style={{ backgroundColor: '#FBF7EF' }}>
      <ToastContainer position="top-right" autoClose={5000} />

      <div className="flex justify-between items-center mb-8">
        <h1 className="font-sans text-4xl text-stone-900">Admin Panel</h1>
        {loading && <p className="font-nunito text-amber-700">Chargement...</p>}
        <button
          onClick={handleLogout}
          className="bg-red-700 text-white py-2.5 px-5 rounded-xl hover:bg-red-800 transition"
        >
          Déconnexion
        </button>
      </div>

      {/* --- Ajouter un événement --- */}
      <div className="bg-white p-6 md:p-8 rounded-2xl border border-[rgba(120,90,40,0.15)] shadow-sm mb-8">
        <h2 className="font-sans text-2xl text-stone-900 mb-5">Ajouter un événement</h2>
        <form onSubmit={handleEventSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Titre de l'événement *"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            value={newEvent.title}
            onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
          />
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="date"
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition flex-1"
              value={newEvent.startDate}
              onChange={(e) => setNewEvent({ ...newEvent, startDate: e.target.value })}
            />
            <input
              type="date"
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition flex-1"
              value={newEvent.endDate}
              onChange={(e) => setNewEvent({ ...newEvent, endDate: e.target.value })}
            />
          </div>
          <input
            type="time"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            value={newEvent.time}
            onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
          />
          <input
            type="text"
            placeholder="Lieu *"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            value={newEvent.location}
            onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
          />
          <textarea
            placeholder="Description"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            rows="3"
            value={newEvent.description}
            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
          />

          {/* Image de l'evenement (optionnelle) */}
          <div>
            <label className="block mb-1 font-medium">Image (optionnelle)</label>
            <input type="file" accept="image/*" onChange={handleEventImage} />
            {imageUploading && (
              <p className="text-sm text-gray-500 mt-1">Upload en cours…</p>
            )}
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Aperçu"
                className="h-28 mt-3 rounded-lg object-cover"
              />
            )}
          </div>

          <button
            type="submit"
            disabled={eventSubmitting || imageUploading}
            className={`bg-[#2E2318] text-white py-2.5 px-5 rounded-xl hover:opacity-90 transition ${
              eventSubmitting || imageUploading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {eventSubmitting ? "Ajout en cours..." : "Ajouter"}
          </button>
        </form>
      </div>

      {/* --- Ajouter un point de vente --- */}
      <div className="bg-white p-6 md:p-8 rounded-2xl border border-[rgba(120,90,40,0.15)] shadow-sm">
        <h2 className="font-sans text-2xl text-stone-900 mb-5">Ajouter un point de vente</h2>
        <form onSubmit={handleSalesPointSubmit} className="space-y-4">
          <input
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            placeholder="Nom *"
            value={newSalesPoint.name}
            onChange={(e) => setNewSalesPoint({ ...newSalesPoint, name: e.target.value })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
              placeholder="Adresse (numéro + voie) *"
              value={newSalesPoint.address1}
              onChange={(e) =>
                setNewSalesPoint({ ...newSalesPoint, address1: e.target.value })
              }
            />
            <input
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
              placeholder="Complément (bât, interphone...)"
              value={newSalesPoint.address2}
              onChange={(e) =>
                setNewSalesPoint({ ...newSalesPoint, address2: e.target.value })
              }
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <input
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
              placeholder="Code postal *"
              value={newSalesPoint.postalCode}
              onChange={(e) =>
                setNewSalesPoint({ ...newSalesPoint, postalCode: e.target.value })
              }
            />
            <input
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition col-span-2"
              placeholder="Ville *"
              value={newSalesPoint.city}
              onChange={(e) =>
                setNewSalesPoint({ ...newSalesPoint, city: e.target.value })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
              placeholder="Pays"
              value={newSalesPoint.country}
              onChange={(e) =>
                setNewSalesPoint({ ...newSalesPoint, country: e.target.value })
              }
            />
            <input
              className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
              placeholder="Catégorie (ex: cave, restaurant, supermarche)"
              value={newSalesPoint.category}
              onChange={(e) =>
                setNewSalesPoint({ ...newSalesPoint, category: e.target.value })
              }
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleVerifyAddress}
              className="bg-stone-700 text-white py-2.5 px-5 rounded-xl hover:bg-stone-800 transition"
            >
              Vérifier sur la carte
            </button>
            <button
              type="submit"
              disabled={salesPointSubmitting}
              className={`bg-[#4F7A3A] text-white py-2.5 px-5 rounded-xl hover:opacity-90 transition ${
                salesPointSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {salesPointSubmitting ? "Ajout en cours..." : "Ajouter"}
            </button>
          </div>

          {isProbableDuplicate() && (
            <p className="text-sm text-amber-600">
              ⚠️ Doublon probable à moins de 100 m dans le même code postal.
            </p>
          )}
        </form>

        {/* Résultats geocode et modal de confirmation */}
        {verifyOpen && (
          <>
            <div className="bg-white rounded-xl border border-[rgba(120,90,40,0.15)] p-4 my-4">
              <p className="mb-2 font-medium">Résultats trouvés :</p>
              <ul className="space-y-1 max-h-32 overflow-auto">
                {candidates.map((c) => (
                  <li key={c.place_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedCandidate(c)}
                      className={`text-left w-full px-2 py-1 rounded ${
                        selectedCandidate?.place_id === c.place_id
                          ? "bg-blue-50"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      {c.formatted_address}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <MapConfirmModal
              open={verifyOpen}
              candidate={selectedCandidate}
              onClose={() => setVerifyOpen(false)}
              onConfirm={applyConfirmation}
            />
          </>
        )}
      </div>

      {/* --- Liste des événements --- */}
      <div className="mb-8 pt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-sans text-2xl text-stone-900">Événements ajoutés</h2>
          <input
            type="text"
            placeholder="Rechercher un événement…"
            className="rounded-xl border border-stone-300 bg-white px-4 py-2.5 w-64 font-nunito focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            value={eventSearch}
            onChange={(e) => setEventSearch(e.target.value)}
          />
        </div>
        <ul className="space-y-3 max-h-[300px] overflow-y-auto rounded-2xl border border-[rgba(120,90,40,0.15)] p-3 bg-white">
          {filteredEvents.map((event) => (
            <li key={event.id} className="p-4 rounded-xl border border-[rgba(120,90,40,0.15)] bg-white relative">
              <h3 className="font-bold">{event.title}</h3>
              <p>
                Dates : {event.startDate} {event.endDate ? ` - ${event.endDate}` : ""}
              </p>
              <p>
                Durée :{" "}
                {event.duration
                  ? `${event.duration} jour${event.duration > 1 ? "s" : ""}`
                  : "N/A"}
              </p>
              <p>Heure : {event.time}</p>
              <p>Lieu : {event.location}</p>
              <p>Description : {event.description}</p>
              <button
                onClick={() => handleDeleteEvent(event.id)}
                disabled={deletingEventId === event.id}
                className={`absolute top-2 right-2 bg-red-600 text-white py-1.5 px-3 rounded-lg hover:bg-red-700 transition ${
                  deletingEventId === event.id ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {deletingEventId === event.id ? "Suppression..." : "Supprimer"}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* --- Liste des points de vente --- */}
      <div className="pt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-sans text-2xl text-stone-900">Points de vente ajoutés</h2>
          <input
            type="text"
            placeholder="Rechercher un point de vente…"
            className="rounded-xl border border-stone-300 bg-white px-4 py-2.5 w-64 font-nunito focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            value={salesPointSearch}
            onChange={(e) => setSalesPointSearch(e.target.value)}
          />
        </div>

        <ul className="space-y-3 max-h-[300px] overflow-y-auto rounded-2xl border border-[rgba(120,90,40,0.15)] p-3 bg-white">
          {filteredSalesPoints.map((point) => (
            <li key={point.id} className="p-4 rounded-xl border border-[rgba(120,90,40,0.15)] bg-white relative">
              <h3 className="font-bold">{point.name}</h3>
              <p>
                {/* Compat: address (legacy) ou address1/address2 */}
                {point.address ||
                  [point.address1, point.address2].filter(Boolean).join(", ")}
              </p>
              <p>
                {point.postalCode ? `${point.postalCode} ${point.city}` : point.city}
              </p>
              <p>Catégorie : {point.category || "—"}</p>
              <p>
                Lat: {typeof point.lat === "number" ? point.lat : "N/A"}, Lng:{" "}
                {typeof point.lng === "number" ? point.lng : "N/A"}
              </p>
              <button
                onClick={() => handleDeleteSalesPoint(point.id)}
                disabled={deletingSalesPointId === point.id}
                className={`absolute top-2 right-2 bg-red-600 text-white py-1.5 px-3 rounded-lg hover:bg-red-700 transition ${
                  deletingSalesPointId === point.id ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {deletingSalesPointId === point.id ? "Suppression..." : "Supprimer"}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AdminPage;